import {
  FUNNEL_STAGES,
  METRIC_LABELS,
  type FunnelMetrics,
  type MetricKey,
  type Semaphore,
  monthsBetween,
  areConsecutiveMonths,
} from '@performance-crm/shared';
import { RefreshCw, Target } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useCompanyScope } from '@/lib/useCompanyScope';
import { MonthPicker } from '@/components/funnel/MonthPicker';
import { formatMonthsLabel } from '@/lib/months';
import { TargetsDialog } from '@/components/funnel/TargetsDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { useAuth } from '@/lib/auth-context';
import { fmt } from '@/lib/format';
import { useAdvisors, useFunnelSummary, useProjects, useRecalculateFunnel } from '@/lib/queries';
import { MANAGER_ROLES } from '@performance-crm/shared';

const PALETTE = [
  '#2563eb',
  '#7c3aed',
  '#059669',
  '#d97706',
  '#dc2626',
  '#0891b2',
  '#4f46e5',
  '#65a30d',
  '#db2777',
  '#475569',
];
const SEM: Record<Semaphore, { tone: 'success' | 'warning' | 'danger' | 'muted'; label: string }> =
  {
    VERDE: { tone: 'success', label: 'Verde' },
    AMBAR: { tone: 'warning', label: 'Ámbar' },
    ROJO: { tone: 'danger', label: 'Rojo' },
    SIN_DATOS: { tone: 'muted', label: 'Sin datos' },
  };
const pct = (v: number | null) =>
  v === null ? '—' : `${(v * 100).toFixed(v * 100 >= 10 ? 0 : 1)}%`;

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
const monthLabel = (m: string) =>
  new Intl.DateTimeFormat('es-PE', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${m}-15T00:00:00Z`),
  );

export function FunnelPage() {
  const { user } = useAuth();
  const { companies, active } = useCompanyScope();
  const [params, setParams] = useSearchParams();
  const companyId = params.get('companyId') ?? active ?? companies[0]?.companyId;
  const projectId = params.get('projectId') ?? undefined;
  const advisorId = params.get('advisorId') ?? undefined;
  // Meses elegidos con casillas (pueden ser salteados). Los enlaces antiguos con month / monthTo siguen valiendo.
  const selected = useMemo(() => {
    const raw = params.get('months');
    if (raw) {
      const list = [...new Set(raw.split(',').filter((m) => /^\d{4}-\d{2}$/.test(m)))].sort();
      if (list.length) return list;
    }
    const m = params.get('month') ?? currentMonth();
    const to = params.get('monthTo') ?? '';
    return to > m ? monthsBetween(m, to) : [m];
  }, [params]);
  const month = selected[0]!;
  const isRange = selected.length > 1;
  const contiguous = areConsecutiveMonths(selected);
  const periodWord = isRange ? 'periodo' : 'mes';
  const periodLabel = formatMonthsLabel(selected);
  const monthsParam = selected.join(',');
  const { data: projects } = useProjects();
  const { data: advisors } = useAdvisors(companyId);
  const { data, isLoading, error } = useFunnelSummary({
    companyId,
    projectId,
    advisorId,
    month,
    months: isRange ? monthsParam : undefined,
  });
  const previousLabel =
    data?.previousKind === 'MISMOS_MESES_ANO_ANTERIOR'
      ? 'vs mismos meses del año anterior'
      : `vs ${periodWord} anterior`;
  const recalc = useRecalculateFunnel();
  const toast = useToast();
  const [targetsOpen, setTargetsOpen] = useState(false);
  const isManager = !!user?.memberships.some(
    (m) => m.companyId === companyId && MANAGER_ROLES.includes(m.role),
  );
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    // Proyecto y asesor son de la empresa: al cambiarla se limpian (si no, el Embudo pide un asesor de otra empresa).
    if (k === 'companyId') {
      next.delete('projectId');
      next.delete('advisorId');
    }
    setParams(next, { replace: true });
  };
  const setMonths = (list: string[]) => {
    if (list.length === 0) return;
    const next = new URLSearchParams(params);
    next.delete('month');
    next.delete('monthTo');
    next.set('months', list.join(','));
    setParams(next, { replace: true });
  };
  const detailsLink = (metric: MetricKey, extra: Record<string, string> = {}) => {
    const p = new URLSearchParams({
      companyId: companyId ?? '',
      month,
      ...(isRange ? { months: monthsParam } : {}),
      metric,
      ...(projectId ? { projectId } : {}),
      ...(advisorId ? { advisorId } : {}),
      ...extra,
      // Estado del Embudo al salir: «← Embudo» del detalle vuelve exactamente aquí (sin el asesor o proyecto de la fila).
      from: params.toString(),
    });
    return `/embudo/detalle?${p}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Embudo</h1>
          <p className="text-muted-foreground text-sm">
            {data?.computedAt
              ? `${periodLabel} · calculado ${fmt.ago(data.computedAt)} · ${Math.round(data.elapsedRatio * 100)}% del ${periodWord} transcurrido`
              : 'Métricas precalculadas por día'}
            {data?.advisorId && ' · solo tus leads'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isManager && (
            <>
              <Button variant="outline" size="sm" onClick={() => setTargetsOpen(true)}>
                <Target className="h-4 w-4" /> Metas
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={recalc.isPending || !companyId}
                onClick={async () => {
                  if (!companyId) return;
                  // Con varios meses se recalcula cada uno.
                  for (const m of selected) await recalc.mutateAsync({ companyId, month: m });
                  toast.success('Recalculando', 'Las cifras se actualizan en unos segundos.');
                }}
              >
                <RefreshCw className="h-4 w-4" /> Recalcular
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-card grid gap-2 rounded-lg border p-3 md:grid-cols-4">
        {companies.length > 1 && (
          <Select
            value={companyId ?? ''}
            onChange={(e) => set('companyId', e.target.value)}
            aria-label="Empresa"
          >
            {companies.map((c) => (
              <option key={c.companyId} value={c.companyId}>
                {c.companyName}
              </option>
            ))}
          </Select>
        )}
        <Select
          value={projectId ?? ''}
          onChange={(e) => set('projectId', e.target.value)}
          aria-label="Proyecto"
        >
          <option value="">Todos los proyectos</option>
          {projects
            ?.filter((p) => p.companyId === companyId)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </Select>
        <MonthPicker value={selected} onChange={setMonths} />
        {isManager && advisors && (
          <Select
            value={advisorId ?? ''}
            onChange={(e) => set('advisorId', e.target.value)}
            aria-label="Asesor"
          >
            <option value="">Todos los asesores</option>
            {advisors
              .filter((a) => a.kind === 'HUMAN')
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.fullName}
                </option>
              ))}
          </Select>
        )}
      </div>

      {error && (
        <p role="alert" className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
          {(error as Error).message}
        </p>
      )}
      {isLoading && <Skeleton className="h-64 w-full" />}

      {data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle data-testid="funnel-title">Embudo del {periodWord}</CardTitle>
              <CardDescription>
                {isRange
                  ? `${data.months} meses (${periodLabel}): sumas del periodo, porcentajes sobre esas sumas y promedio por mes. ${contiguous ? '' : 'Como los meses no van seguidos, se compara con los mismos meses del año anterior. '}`
                  : ''}
                Cada etapa se imputa al mes de su propia fecha. Haz clic en una cifra para ver el
                detalle fila por fila.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FunnelBars
                totals={data.totals}
                previous={data.previous}
                average={isRange ? data.monthlyAverage : null}
                periodWord={periodWord}
                previousLabel={previousLabel}
                link={detailsLink}
              />
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Real vs meta</CardTitle>
                <CardDescription>
                  Esperado = meta × {Math.round(data.elapsedRatio * 100)}% del {periodWord}{' '}
                  transcurrido.{isRange ? ' Las metas son la suma de los meses del periodo.' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data.targets.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    Sin metas para este {periodWord}.{' '}
                    {isManager && 'Defínelas con el botón "Metas".'}
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-muted-foreground text-left text-xs uppercase">
                      <tr>
                        <th className="py-1">Métrica</th>
                        <th className="py-1 text-right">Real</th>
                        <th className="py-1 text-right">Meta</th>
                        <th className="py-1 text-right">Esperado</th>
                        <th className="py-1 text-right">Avance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.targets.map((t) => (
                        <tr key={t.metric} className="border-t" data-testid="target-row">
                          <td className="py-1.5">{METRIC_LABELS[t.metric]}</td>
                          <td className="py-1.5 text-right font-medium">{t.real}</td>
                          <td className="py-1.5 text-right">{t.target}</td>
                          <td className="text-muted-foreground py-1.5 text-right">{t.expected}</td>
                          <td className="py-1.5 text-right">
                            <Badge tone={SEM[t.status].tone}>{pct(t.achieved)}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Ratios y semáforo</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3 text-sm">
                <Ratio
                  label="% contacto efectivo"
                  value={data.ratios.contactoEfectivo}
                  sem={data.semaphores.contactoEfectivo}
                />
                <Ratio
                  label="% recontacto"
                  value={data.ratios.recontacto}
                  sem={data.semaphores.recontacto}
                />
                <Ratio
                  label="% citas concretadas"
                  value={data.ratios.citasConcretadas}
                  sem={data.semaphores.citasConcretadas}
                />
                <Ratio
                  label="Visitas / captaciones"
                  value={data.ratios.visitasSobreCaptaciones}
                  sem={data.semaphores.visitasSobreCaptaciones}
                />
                <Ratio
                  label="Separaciones / visitas"
                  value={data.ratios.separacionesSobreVisitas}
                  sem={data.semaphores.separacionesSobreVisitas}
                />
                <Ratio
                  label="Ventas / captaciones"
                  value={data.ratios.ventasSobreCaptaciones}
                  sem={data.semaphores.ventasSobreCaptaciones}
                />
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Tendencia diaria</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={data.daily.map((d) => ({
                      day: isRange ? d.date.slice(5) : d.date.slice(8),
                      ...d.metrics,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" fontSize={11} />
                    <YAxis fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="CAPTACIONES"
                      name="Captaciones"
                      stroke={PALETTE[0]}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="CONTACTOS_EFECTIVOS"
                      name="Contactos efectivos"
                      stroke={PALETTE[1]}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="VISITAS"
                      name="Visitas"
                      stroke={PALETTE[2]}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{isRange ? 'Meses del periodo' : 'Últimos 6 meses'}</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.monthly.map((m) => ({ month: monthLabel(m.month), ...m.metrics }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" fontSize={11} />
                    <YAxis fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="VISITAS" name="Visitas" fill={PALETTE[2]} />
                    <Bar dataKey="SEPARACIONES" name="Separaciones" fill={PALETTE[3]} />
                    <Bar dataKey="VENTAS" name="Ventas" fill={PALETTE[0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Pie de medios</CardTitle>
                <CardDescription>
                  Captaciones por categoría. Los únicos por medio no suman el total global.
                </CardDescription>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.byCategory.map((c) => ({
                        name: c.category,
                        value: c.metrics.CAPTACIONES,
                      }))}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={45}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {data.byCategory.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Por medio</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <MetricsTable
                  rows={data.byCategory.map((c) => ({
                    key: c.category,
                    label: `${c.category}${c.isDigital ? ' · digital' : ''}`,
                    metrics: c.metrics,
                    link: (m: MetricKey) => detailsLink(m, { mediumCategory: c.category }),
                  }))}
                  keys={[
                    'CAPTACIONES',
                    'LEADS_UNICOS',
                    'CONTACTOS_EFECTIVOS',
                    'CITAS_GENERADAS',
                    'VISITAS',
                    'SEPARACIONES',
                    'VENTAS',
                  ]}
                />
              </CardContent>
            </Card>
          </div>

          {data.byAdvisor.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Gestión comercial por asesor</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <MetricsTable
                  rows={data.byAdvisor.map((a) => ({
                    key: a.advisorId,
                    label: a.advisorName,
                    metrics: a.metrics,
                    link: (m: MetricKey) => detailsLink(m, { advisorId: a.advisorId }),
                  }))}
                  keys={[
                    'CAPTACIONES',
                    'LEADS_UNICOS',
                    'CONTACTOS_TOTAL',
                    'CONTACTOS_EFECTIVOS',
                    'CITAS_GENERADAS',
                    'CITAS_CONCRETADAS',
                    'VISITAS',
                    'SEPARACIONES',
                    'VENTAS',
                  ]}
                />
              </CardContent>
            </Card>
          )}
          {!projectId && data.byProject.length > 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Por proyecto</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <MetricsTable
                  rows={data.byProject.map((p) => ({
                    key: p.projectId,
                    label: p.projectName,
                    metrics: p.metrics,
                    link: (m: MetricKey) => detailsLink(m, { projectId: p.projectId }),
                  }))}
                  keys={[
                    'CAPTACIONES',
                    'LEADS_UNICOS',
                    'CONTACTOS_EFECTIVOS',
                    'CITAS_GENERADAS',
                    'CITAS_CONCRETADAS',
                    'VISITAS',
                    'TRANSITO',
                    'SEPARACIONES',
                    'VENTAS',
                  ]}
                />
              </CardContent>
            </Card>
          )}
        </>
      )}
      {companyId && (
        <TargetsDialog
          companyId={companyId}
          month={month}
          open={targetsOpen}
          onOpenChange={setTargetsOpen}
        />
      )}
    </div>
  );
}

function FunnelBars({
  totals,
  previous,
  average,
  periodWord,
  previousLabel,
  link,
}: {
  totals: FunnelMetrics;
  previous: FunnelMetrics;
  /** Promedio por mes (solo con un rango de meses). */
  average: FunnelMetrics | null;
  periodWord: string;
  /** "vs periodo anterior" o "vs mismos meses del año anterior". */
  previousLabel: string;
  link: (m: MetricKey) => string;
}) {
  const max = Math.max(1, ...FUNNEL_STAGES.map((s) => totals[s.key]));
  const cols = average ? 'grid-cols-[9rem_1fr_6rem_6rem_5rem]' : 'grid-cols-[9rem_1fr_6rem_5rem]';
  return (
    <ol className="space-y-2" data-testid="funnel-bars">
      <li
        className={`text-muted-foreground grid ${cols} items-end gap-3 text-[11px] font-medium uppercase tracking-wide`}
        aria-hidden="true"
      >
        <span>Etapa</span>
        <span>Cantidad del {periodWord}</span>
        <span className="text-right">% de la etapa previa</span>
        {average && <span className="text-right">Promedio mensual</span>}
        <span className="text-right">{previousLabel}</span>
      </li>
      {FUNNEL_STAGES.map((s, i) => {
        const value = totals[s.key];
        const prev = FUNNEL_STAGES[i - 1] ? totals[FUNNEL_STAGES[i - 1]!.key] : null;
        const conv = prev ? value / prev : null;
        const delta = previous[s.key] ? (value - previous[s.key]) / previous[s.key] : null;
        return (
          <li key={s.key} className={`grid ${cols} items-center gap-3 text-sm`}>
            <span className="font-medium">{s.label}</span>
            <div className="bg-muted h-7 rounded">
              <div
                className="bg-primary/80 text-primary-foreground flex h-7 items-center rounded px-2 text-xs font-semibold"
                style={{ width: `${Math.max(4, (value / max) * 100)}%` }}
              >
                <Link to={link(s.key)} className="hover:underline" data-testid={`funnel-${s.key}`}>
                  {fmt.number(value)}
                </Link>
              </div>
            </div>
            <span className="text-muted-foreground text-right text-xs">
              {conv !== null ? `${pct(conv)} de la etapa previa` : ''}
            </span>
            {average && (
              <span className="text-right text-xs tabular-nums" data-testid="funnel-average">
                {average[s.key].toLocaleString('es-PE', { maximumFractionDigits: 1 })} / mes
              </span>
            )}
            <span
              className={`text-right text-xs ${delta === null ? 'text-muted-foreground' : delta >= 0 ? 'text-success' : 'text-danger'}`}
              title={previousLabel}
            >
              {delta === null ? '—' : `${delta >= 0 ? '+' : ''}${Math.round(delta * 100)}%`}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Ratio({ label, value, sem }: { label: string; value: number | null; sem: Semaphore }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-muted-foreground text-xs">{label}</p>
      <div className="mt-1 flex items-center justify-between">
        <span className="text-xl font-semibold">{pct(value)}</span>
        <Badge tone={SEM[sem].tone}>{SEM[sem].label}</Badge>
      </div>
    </div>
  );
}

function MetricsTable({
  rows,
  keys,
}: {
  rows: Array<{
    key: string;
    label: string;
    metrics: FunnelMetrics;
    link: (m: MetricKey) => string;
  }>;
  keys: MetricKey[];
}) {
  return (
    <table className="w-full text-sm">
      <thead className="text-muted-foreground text-left text-xs uppercase">
        <tr>
          <th className="py-1 pr-2"></th>
          {keys.map((k) => (
            <th key={k} className="py-1 pr-2 text-right">
              {METRIC_LABELS[k]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-t">
            <td className="py-1.5 pr-2 font-medium">{r.label}</td>
            {keys.map((k) => (
              <td key={k} className="py-1.5 pr-2 text-right">
                <Link to={r.link(k)} className="hover:underline">
                  {fmt.number(r.metrics[k])}
                </Link>
              </td>
            ))}
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={keys.length + 1} className="text-muted-foreground py-3 text-center">
              Sin datos
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
